<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('componentEditSpc.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditComponents">{{ $t('componentEditSpc.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('componentEditSpc.title') }}</li>
                    </ol>
                </div>
            </div>

            <!-- Formulaire -->
            <form ref="formRef" id="spcForm" name="spcForm" @submit.prevent="handleSubmit" class="pb-4">
                <div class="row">
                    <div class="col-xxl-8 col-xl-10">
                        <div class="card">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('componentEditSpc.section') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="mb-2 col-md-6">
                                        <label for="spc-receiver-id" class="form-label">Receiver ID</label>
                                        <input type="text" id="spc-receiver-id" class="form-control" inputmode="numeric" maxlength="10"
                                            :value="form.receiver_id" @input="onDigitsInput('receiver_id', $event)"
                                            :placeholder="$t('componentEditSpc.receiverIdPlaceholder')" autocomplete="off" required />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="spc-id" class="form-label">ID</label>
                                        <input type="text" id="spc-id" class="form-control" maxlength="64" v-model="form.id"
                                            :placeholder="$t('componentEditSpc.idPlaceholder')" autocomplete="off" required />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="spc-serial" class="form-label">{{ $t('common.serialNumber') }}</label>
                                        <input type="text" id="spc-serial" class="form-control" inputmode="numeric" maxlength="18"
                                            :value="form.serial" @input="onDigitsInput('serial', $event)"
                                            :placeholder="$t('componentEditSpc.serialPlaceholder')" autocomplete="off" />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="spc-spt-account" class="form-label">SPT account</label>
                                        <input type="text" id="spc-spt-account" class="form-control" inputmode="numeric" maxlength="10"
                                            :value="form.spt_account" @input="onDigitsInput('spt_account', $event)"
                                            :placeholder="$t('componentEditSpc.sptAccountPlaceholder')" autocomplete="off" />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="spc-aes-key" class="form-label">AES key</label>
                                        <div class="input-group">
                                            <input type="text" id="spc-aes-key" class="form-control" maxlength="64" v-model="form.aes_key"
                                                :placeholder="$t('componentEditSpc.aesKeyPlaceholder')" autocomplete="off" required />
                                            <button v-if="canPaste" type="button" class="btn btn-light border"
                                                :title="$t('common.paste')" :aria-label="$t('common.paste')" @click="paste('aes_key')">
                                                <i class="mdi mdi-content-paste"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="spc-aes-iv" class="form-label">AES IV</label>
                                        <div class="input-group">
                                            <input type="text" id="spc-aes-iv" class="form-control" maxlength="64" v-model="form.aes_iv"
                                                :placeholder="$t('componentEditSpc.aesIvPlaceholder')" autocomplete="off" />
                                            <button v-if="canPaste" type="button" class="btn btn-light border"
                                                :title="$t('common.paste')" :aria-label="$t('common.paste')" @click="paste('aes_iv')">
                                                <i class="mdi mdi-content-paste"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="spc-username" class="form-label">{{ $t('componentEditSpc.username') }}</label>
                                        <input type="text" id="spc-username" class="form-control" maxlength="64" v-model="form.username"
                                            :placeholder="$t('componentEditSpc.usernamePlaceholder')" autocomplete="off" required />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <PasswordComponent id="spc-password" v-model="form.password" tabindex="0" />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="spc-access-control" class="form-label">{{ $t('componentEditSpc.accessControl') }}</label>
                                        <select id="spc-access-control" class="form-select" v-model="form.access_control">
                                            <option :value="true">{{ $t('common.yes') }}</option>
                                            <option :value="false">{{ $t('common.no') }}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Form actions -->
                <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                    <router-link :to="pathToInstallationEditComponents" class="btn btn-outline-secondary me-2">{{ $t('common.back') }}</router-link>
                    <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { computed, nextTick, reactive, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useI18n } from 'vue-i18n';
import { showToast } from '@/services/toast';
import PasswordComponent from '@/components/password.vue';
import SpcConfig from '@/models/spcConfig';

const MAX_UINT32 = 4294967295;

export default {
    name: 'ComponentEditSpc',
    components: {
        PasswordComponent,
    },
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();
        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.hubId);
        const hub = userStore.getHub(facilityId, hubId);
        const config = hub.spc_config;
        const formRef = ref(null);

        // Prefilled with the configuration saved on the cloud
        const form = reactive({
            receiver_id: config ? String(config.receiver_id) : '',
            id: config ? config.id : '',
            serial: config && config.serial != null ? String(config.serial) : '',
            spt_account: config && config.spt_account != null ? String(config.spt_account) : '',
            aes_key: config ? config.aes_key : '',
            aes_iv: config && config.aes_iv ? config.aes_iv : '',
            username: config ? config.username : '',
            password: config ? config.password : '',
            access_control: config ? config.access_control === true : false,
        });

        // Digits only, as in the app
        const onDigitsInput = (field, event) => {
            const digits = event.target.value.replace(/\D/g, '');
            event.target.value = digits;
            event.target.setCustomValidity('');
            form[field] = digits;
        };

        // The clipboard API only exists on secure origins
        const canPaste = Boolean(navigator.clipboard && navigator.clipboard.readText);

        const paste = async (field) => {
            try {
                const text = await navigator.clipboard.readText();
                if (text) form[field] = text;
            } catch (error) {
                console.error('Clipboard reading failed', error);
            }
        };

        const displayError = (id) => {
            const input = document.getElementById(id);
            input.setCustomValidity(t('componentEditSpc.checkInput'));
            input.reportValidity();
        };

        // Unsigned 32 bits values as in the app. The serial number must also stay below
        // Number.MAX_SAFE_INTEGER, otherwise JavaScript would round it before sending it
        const validateNumbers = () => {
            if (Number(form.receiver_id) > MAX_UINT32) {
                displayError('spc-receiver-id');
                return false;
            }
            if (form.serial && !Number.isSafeInteger(Number(form.serial))) {
                displayError('spc-serial');
                return false;
            }
            if (form.spt_account && Number(form.spt_account) > MAX_UINT32) {
                displayError('spc-spt-account');
                return false;
            }
            return true;
        };

        const pathToInstallationEditComponents = computed(() => {
            return `/installation-edit?id=${facilityId}&action=components`;
        });

        const handleSubmit = async () => {
            // As in the app, fields are trimmed before validation, except the password
            for (const field of ['receiver_id', 'id', 'serial', 'spt_account', 'aes_key', 'aes_iv', 'username']) {
                form[field] = form[field].trim();
            }
            await nextTick();
            if (!formRef.value.reportValidity() || !validateNumbers()) return;

            const newConfig = Object.assign(new SpcConfig(), {
                receiver_id: Number(form.receiver_id),
                id: form.id,
                serial: form.serial ? Number(form.serial) : null,
                spt_account: form.spt_account ? Number(form.spt_account) : null,
                aes_key: form.aes_key,
                aes_iv: form.aes_iv || null,
                username: form.username,
                password: form.password,
                access_control: form.access_control,
            });

            try {
                await userStore.configureSpc(facilityId, hub, newConfig);
                await router.push(pathToInstallationEditComponents.value);
                showToast('success', t('common.updateSuccess'));
            } catch (error) {
                showToast('error', error);
            }
        };

        return {
            formRef,
            form,
            onDigitsInput,
            canPaste,
            paste,
            pathToInstallationEditComponents,
            handleSubmit,
        };
    },
};
</script>
